import { forwardRef } from 'react'
import { useSelector } from 'react-redux'
import { UploadFlow } from '@vp/upload-flow'
import { v4 as uuid } from 'uuid'

import { ANALYTICS_CATEGORY, ANALYTICS_EVENT_ACTIONS, ANALYTICS_LABEL } from '~/shared/constants'
import { instrumenter } from '~/client/utils/instrumentation'
import {
  getProductKey,
  getProductVersion,
  getLocaleSelector,
  getQuantitySelector,
  getGalleryNameSelector,
  getMpvid,
  getTenant,
  getH1Title,
} from 'client/store/config'
import { useAnalytics } from 'client/hooks/gallery/useAnalytics'
import { userOptionsSelector } from 'client/store/refinement/selectors'
import { Tile } from '~/client/components/common/Tile'
import { UploadTile } from './UploadTile'
import { useIdentity } from '@vp/ubik-context'
import { usePricingContext } from 'client/contexts/pricing/context'

export type PropTypes = Gallery.Analytics.ButtonLocationProp &
  React.HTMLProps<HTMLElement> & {
    productOptionsByKey: Gallery.Product.ProductOptionsGroupsByProductKey | null
    pricingPresentationType?: string | null
  }

export const FullBleedUploadTile = forwardRef<HTMLLIElement, PropTypes>((props: PropTypes, ref) => {
  /**
   * Selectors
   */
  const analytics = useAnalytics()

  const tenant = useSelector(getTenant)
  const locale = useSelector(getLocaleSelector)
  const mpvId = useSelector(getMpvid)
  const quantity = useSelector(getQuantitySelector)
  const productKey = useSelector(getProductKey)
  const name = useSelector(getGalleryNameSelector)
  const title = useSelector(getH1Title)
  const userOptions = useSelector(userOptionsSelector)
  const productVersion = useSelector(getProductVersion)

  const pricingContext = usePricingContext()
  const retrievedPricingContext = pricingContext?.getPricingContext()
  const vatInclusivity = retrievedPricingContext?.vatInclusive || false
  const pricingContextString = pricingContext?.getEncodedContextString()
  const useFullPath = true
  const { pageSection, pageStage, pageName } = analytics.getPageProperties()

  /**
   * Handlers
   */
  const handlePopupOpen = (selections: Record<string, string>): void => {
    analytics.trackEvent({
      userInteractionLocation: props.userInteractionLocation,
      action: ANALYTICS_EVENT_ACTIONS.DESIGN_POPUP_DISPLAYED,
      category: ANALYTICS_CATEGORY.DESIGN_UPLOAD,
      eventLabel: ANALYTICS_LABEL.OPTIONS_DISPLAYED,
      ...analytics.buildFullBleedUploadMetaData(selections),
    })
  }

  const { identity } = useIdentity()

  const handleSubmit = (selections: Record<string, string>): void => {
    instrumenter.recordDesignSelection(selections, 'fbu')

    const fullBleedEngagementId = uuid()

    analytics.trackEvent({
      userInteractionLocation: props.userInteractionLocation,
      action: ANALYTICS_EVENT_ACTIONS.DESIGN_POPUP_DISPLAYED,
      category: ANALYTICS_CATEGORY.DESIGN_UPLOAD,
      eventLabel: ANALYTICS_LABEL.OPTIONS_VALIDATION,
      fullbleedEngagementId: fullBleedEngagementId,
      ...analytics.buildFullBleedUploadMetaData(selections),
    })
  }

  return (
    <Tile className="full-bleed-upload-tile" ref={ref} type="fbu">
      <UploadFlow
        analyticsPageData={{
          pageSection,
          pageStage,
          pageName,
          productName: name,
          coreProductId: productKey,
          position: 0,
        }}
        authToken={identity?.accessToken ?? undefined}
        loader={<UploadTile {...props} ref={ref} />}
        locale={locale}
        mpvId={mpvId}
        pricingContextString={pricingContextString ?? ''}
        productKey={productKey}
        productName={title}
        productVersion={productVersion ?? 0}
        quantity={quantity as number}
        requestor="gallery6-client"
        tenant={tenant}
        useFullPath={useFullPath}
        userOptions={userOptions as Record<string, string>}
        vatInc={vatInclusivity}
        onPopupOpen={handlePopupOpen}
        onSubmit={handleSubmit}
      >
        <UploadTile {...props} ref={ref} />
      </UploadFlow>
    </Tile>
  )
})

FullBleedUploadTile.displayName = 'FullBleedUploadTile'
